<template>
    <div class="grid">
        <Toast/>
        <ConfirmDialog/>

        <!-- ===== Tesorería ===== -->
        <div class="col-12" style="max-width: 1000px;">
            <div class="card">
                <h4>Mapeos de tesorería</h4>
                <p style="max-width: 720px;">
                    Cada caja, banco o tarjeta tiene su propia cuenta contable, para poder
                    conciliarla por separado. Normalmente se asigna sola al crear la cuenta;
                    aquí puedes corregirla si hace falta.
                </p>
                <Message severity="warn" :closable="false">
                    Reasignar <b>no mueve los asientos ya registrados</b>: el histórico se queda en
                    la cuenta anterior. Hazlo solo si la cuenta aún no tiene movimientos.
                </Message>

                <div style="overflow-x: auto;" class="mt-3">
                <DataTable :value="treasury" :loading="loading">
                    <Column header="Cuenta de tesorería" style="min-width: 200px;">
                        <template #body="slotProps">
                            {{ slotProps.data.name }}
                            <Tag class="ml-2" severity="info"
                                 :value="moneyTypeLabel(slotProps.data.moneyType)"/>
                        </template>
                    </Column>
                    <Column header="Cuenta contable" style="min-width: 320px;">
                        <template #body="slotProps">
                            <Dropdown v-model="slotProps.data.accountId" :options="treasuryAccounts"
                                      optionLabel="label" optionValue="id" style="width: 100%; max-width: 320px;"
                                      @change="saveTreasury(slotProps.data)"/>
                        </template>
                    </Column>
                </DataTable>
                </div>
            </div>
        </div>

        <!-- ===== Categorías de producto ===== -->
        <div class="col-12" style="max-width: 1000px;" v-if="categories.length">
            <div class="card">
                <h4>Categorías de producto</h4>
                <p style="max-width: 720px;">
                    Permite separar tus ventas, tu inventario y tu costo por tipo de producto:
                    ver cuánto vendiste en bebidas frente a alimentos, por ejemplo. Lo que dejes
                    sin asignar va a las cuentas generales, que es como funciona hoy.
                </p>
                <p style="max-width: 720px;">
                    <b>Consumo</b> es lo que se usó de los insumos de conteo periódico (sal,
                    limpieza): la sal es costo, la limpieza va mejor en Suministros y
                    materiales. Sin asignar, se registra igual que el costo de ventas.
                </p>

                <!-- Cuatro desplegables de cuenta en una fila: con anchos fijos no caben
                     en la tarjeta. Van fluidos y la tabla scrollea sola en pantallas
                     angostas, en vez de desbordar la pagina. -->
                <div style="overflow-x: auto;" class="mt-3">
                <DataTable :value="categoryRows">
                    <Column field="name" header="Categoría" style="min-width: 130px;"></Column>
                    <Column header="Ventas" style="min-width: 180px;">
                        <template #body="slotProps">
                            <Dropdown v-model="slotProps.data.SALES" :options="roleAccounts.SALES"
                                      optionLabel="label" optionValue="id" showClear
                                      placeholder="Cuenta general" style="width: 100%;"
                                      @change="saveCategory(slotProps.data, 'SALES')"/>
                        </template>
                    </Column>
                    <Column header="Inventario" style="min-width: 180px;">
                        <template #body="slotProps">
                            <Dropdown v-model="slotProps.data.INVENTORY" :options="roleAccounts.INVENTORY"
                                      optionLabel="label" optionValue="id" showClear
                                      placeholder="Cuenta general" style="width: 100%;"
                                      @change="saveCategory(slotProps.data, 'INVENTORY')"/>
                        </template>
                    </Column>
                    <Column header="Costo de ventas" style="min-width: 180px;">
                        <template #body="slotProps">
                            <Dropdown v-model="slotProps.data.COGS" :options="roleAccounts.COGS"
                                      optionLabel="label" optionValue="id" showClear
                                      placeholder="Cuenta general" style="width: 100%;"
                                      @change="saveCategory(slotProps.data, 'COGS')"/>
                        </template>
                    </Column>
                    <Column header="Consumo" style="min-width: 180px;">
                        <template #body="slotProps">
                            <Dropdown v-model="slotProps.data.CONSUMPTION" :options="roleAccounts.CONSUMPTION"
                                      optionLabel="label" optionValue="id" showClear
                                      placeholder="Igual que el costo" style="width: 100%;"
                                      @change="saveCategory(slotProps.data, 'CONSUMPTION')"/>
                        </template>
                    </Column>
                </DataTable>
                </div>
            </div>
        </div>

        <!-- ===== Categorías de egreso ===== -->
        <div class="col-12" style="max-width: 1000px;">
            <div class="card">
                <h4>Categorías de egreso</h4>
                <p style="max-width: 720px;">
                    Cuando registras un gasto sin inventario, su categoría decide en qué cuenta
                    de gasto se contabiliza. Las categorías sin mapeo caen en
                    <b>Otros gastos</b>.
                </p>

                <div style="display:flex; gap:0.5rem; align-items:flex-end; flex-wrap:wrap;">
                    <div>
                        <label style="display:block; font-size:0.85rem; color:#777;">Categoría</label>
                        <InputText v-model="newExpenseType" placeholder="viveres, transporte…"
                                   style="width: 220px;"/>
                    </div>
                    <div>
                        <label style="display:block; font-size:0.85rem; color:#777;">Cuenta de gasto</label>
                        <Dropdown v-model="newExpenseAccount" :options="expenseAccounts"
                                  optionLabel="label" optionValue="id" style="width: 100%; max-width: 320px;"/>
                    </div>
                    <Button label="Asignar" icon="pi pi-plus" :loading="working" @click="addExpenseMapping"/>
                </div>

                <div style="overflow-x: auto;" class="mt-3" v-if="expenses.length">
                <DataTable :value="expenses">
                    <Column field="expenseType" header="Categoría" style="min-width: 180px;"></Column>
                    <Column header="Cuenta de gasto" style="min-width: 320px;">
                        <template #body="slotProps">
                            <Dropdown v-model="slotProps.data.accountId" :options="expenseAccounts"
                                      optionLabel="label" optionValue="id" style="width: 100%; max-width: 320px;"
                                      @change="saveExpense(slotProps.data)"/>
                        </template>
                    </Column>
                    <!-- Antes un mapeo no se podía quitar, solo cambiar de cuenta (H67). -->
                    <Column style="width: 1%; white-space: nowrap;">
                        <template #body="slotProps">
                            <Button label="Quitar mapeo" icon="pi pi-times" class="p-button-text p-button-danger p-button-sm"
                                    @click="confirmUnmapExpense(slotProps.data)"/>
                        </template>
                    </Column>
                </DataTable>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import httpRequest from '../../../service/httpRequest';

export default {
    data() {
        return {
            loading: true,
            working: false,
            treasury: [],
            treasuryAccounts: [],
            expenses: [],
            expenseAccounts: [],
            categories: [],
            categoryMappings: [],
            roleAccounts: {SALES: [], INVENTORY: [], COGS: [], CONSUMPTION: []},
            newExpenseType: '',
            newExpenseAccount: null,
        };
    },
    computed: {
        categoryRows() {
            // Una fila por categoría con la cuenta de cada rol, para que se lea como
            // una matriz en vez de una lista de pares sueltos.
            return this.categories.map(category => {
                const row = {id: category.id, name: category.name,
                             SALES: null, INVENTORY: null, COGS: null, CONSUMPTION: null};
                for (const mapping of this.categoryMappings) {
                    if (mapping.categoryId === category.id) row[mapping.role] = mapping.accountId;
                }
                return row;
            });
        },
    },
    async mounted() {
        await this.load();
    },
    methods: {
        moneyTypeLabel(moneyType) {
            return {CASH: 'Efectivo', TRANSFER: 'Banco', CARD: 'Tarjeta'}[moneyType] || moneyType;
        },
        label(account) {
            return account.code + '  ' + account.name;
        },
        async load() {
            this.loading = true;
            const response = await httpRequest.getAccountingMappings();
            this.loading = false;
            if (response.status !== 200) {
                this.$toast.add({severity: 'error', summary: 'Error',
                                 detail: 'No se pudieron cargar los mapeos', life: 4000});
                return;
            }
            const data = response.data;
            this.treasury = data.treasury;
            this.expenses = data.expenses;
            this.treasuryAccounts = data.treasuryAccounts.map(a => ({...a, label: this.label(a)}));
            this.expenseAccounts = data.expenseAccounts.map(a => ({...a, label: this.label(a)}));
            this.categories = data.categories || [];
            this.categoryMappings = data.categoryMappings || [];
            const roles = data.categoryRoleAccounts || {};
            this.roleAccounts = {
                SALES: (roles.SALES || []).map(a => ({...a, label: this.label(a)})),
                INVENTORY: (roles.INVENTORY || []).map(a => ({...a, label: this.label(a)})),
                COGS: (roles.COGS || []).map(a => ({...a, label: this.label(a)})),
                CONSUMPTION: (roles.CONSUMPTION || []).map(a => ({...a, label: this.label(a)})),
            };
        },

        async saveCategory(row, role) {
            // La «X» deja el valor en null: eso NO es un no-op, es quitar el mapeo
            // para que la categoria vuelva a la cuenta general. Se manda igual.
            const accountId = row[role] || null;
            const response = await httpRequest.mapAccountingProductCategory(
                row.id, role, accountId);
            if (response.status === 200) {
                this.$toast.add({
                    severity: 'success',
                    summary: accountId ? 'Mapeo actualizado' : 'Vuelve a la cuenta general',
                    detail: row.name, life: 3000});
                await this.load();
            } else {
                this.$toast.add({severity: 'error', summary: 'No se pudo asignar',
                                 detail: response.error || '', life: 5000});
                await this.load();
            }
        },
        async saveTreasury(row) {
            const response = await httpRequest.remapAccountingTreasury(
                row.finantialAccountId, row.accountId);
            if (response.status === 200) {
                this.$toast.add({severity: 'success', summary: 'Mapeo actualizado',
                                 detail: row.name, life: 3000});
            } else {
                this.$toast.add({severity: 'error', summary: 'No se pudo reasignar',
                                 detail: response.error || '', life: 5000});
                await this.load();   // revierte el dropdown al valor real
            }
        },
        async saveExpense(row) {
            const response = await httpRequest.mapAccountingExpenseType(
                row.expenseType, row.accountId);
            if (response.status === 200) {
                this.$toast.add({severity: 'success', summary: 'Mapeo actualizado',
                                 detail: row.expenseType, life: 3000});
            } else {
                this.$toast.add({severity: 'error', summary: 'No se pudo asignar',
                                 detail: response.error || '', life: 5000});
                await this.load();
            }
        },
        confirmUnmapExpense(row) {
            this.$confirm.require({
                message: '¿Quitar el mapeo de «' + row.expenseType + '»? La categoría deja de ofrecerse en Egresos.',
                header: 'Quitar mapeo',
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: 'Sí',
                rejectLabel: 'No',
                accept: () => this.unmapExpense(row),
            });
        },
        async unmapExpense(row) {
            const response = await httpRequest.unmapAccountingExpenseType(row.expenseType);
            if (response.status === 200) {
                this.$toast.add({severity: 'success', summary: 'Mapeo quitado', detail: row.expenseType, life: 3000});
                await this.load();
            } else {
                this.$toast.add({severity: 'error', summary: 'No se pudo quitar',
                                 detail: response.error || '', life: 5000});
            }
        },
        async addExpenseMapping() {
            if (!this.newExpenseType || !this.newExpenseAccount) {
                this.$toast.add({severity: 'warn', summary: 'Faltan datos',
                                 detail: 'Escribe la categoría y elige la cuenta', life: 4000});
                return;
            }
            this.working = true;
            const response = await httpRequest.mapAccountingExpenseType(
                this.newExpenseType, this.newExpenseAccount);
            this.working = false;
            if (response.status === 200) {
                this.newExpenseType = '';
                this.newExpenseAccount = null;
                await this.load();
            } else {
                this.$toast.add({severity: 'error', summary: 'No se pudo asignar',
                                 detail: response.error || '', life: 5000});
            }
        },
    },
}
</script>
